import React, { useState, useEffect } from 'react'
import omit from 'lodash/omit'
import kebabCase from 'lodash/kebabCase'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import { Form, FormItem } from '@react/react-spectrum/Form'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import Textfield from '@react/react-spectrum/Textfield/js/Textfield'

import { Draggable, DroppableArea } from 'components/DragAndDrop'

import { Screen, ScreenSidebar } from './partials'
import { useScreens, useProject } from 'hooks'

import { FILE_STORAGE } from 'config/constants'

import './styles/ScreenStructure.scss'

const ScreenTemplatePicker = ({ value, allowFreeform, onChange, projectConfig }) => {
  const freeformTemplates = projectConfig.freeformTemplates?.filter((t) => !t.hidden) || []
  const fixedTemplates = projectConfig.screenTemplates?.filter((t) => !t.hidden) || []
  const [selectedTemplate, setSelectedTemplate] = useState(value || projectConfig.defaultTemplate)

  const getItemAttributes = (st) => {
    const isSelected =
      selectedTemplate && st.template === selectedTemplate.template && st.preset === selectedTemplate.preset
    return isSelected ? { 'data-selected': true } : {}
  }

  const selectTemplate = (template) => {
    const filteredTemplate = omit(template, 'label', 'thumbnail')
    setSelectedTemplate(filteredTemplate)
    onChange(filteredTemplate)
  }

  useEffect(() => {
    if (!value) {
      onChange(selectedTemplate)
    } else {
      setSelectedTemplate(omit(value, 'label', 'thumbnail'))
    }
  }, [value, onChange])

  const renderTemplates = (list) =>
    list.map((st) => (
      <div
        key={st.template + '-' + st.preset}
        className='ScreenTemplatePicker__listItem'
        onClick={() => selectTemplate(st)}
      >
        <div className='ScreenTemplatePicker__imageWrapper' {...getItemAttributes(st)}>
          <img
            src={`${FILE_STORAGE.BASE_URL}/${FILE_STORAGE.SCREENS_FOLDER}/${st.thumbnail}`}
            alt={st.template + ' ' + st.preset + ' screen'}
          ></img>
        </div>

        <div className='ScreenTemplatePicker__label'>{st.label}</div>

        {st.description && <div className='ScreenTemplatePicker__description'>{st.description}</div>}
      </div>
    ))

  return (
    <div className='ScreenTemplatePicker'>
      {allowFreeform && !!freeformTemplates.length && (
        <>
          <div>
            <b>FREEFORM TEMPLATES</b>
            <p>Templates with editable components</p>
            <div className='ScreenTemplatePicker__list'>{renderTemplates(freeformTemplates)}</div>
          </div>
          <br />
          <br />
        </>
      )}

      {!!fixedTemplates.length && (
        <div>
          <b>FIXED TEMPLATES</b>
          <small className='ScreenTemplatePicker__comment'></small>
          <p>Templates with only top-level properties</p>
          <div className='ScreenTemplatePicker__list'>{renderTemplates(fixedTemplates)}</div>
        </div>
      )}
    </div>
  )
}

const NewScreenDialog = ({ projectScreens, projectConfig, onSubmit, ...otherProps }) => {
  const [localScreen, setLocalScreen] = useState(projectConfig.defaultTemplate)

  const nameValidationState =
    localScreen.name &&
    !projectScreens.find((s) => s.name === localScreen.name) &&
    /^([a-zA-Z0-9$-_.+!*,]{3,})$/.test(localScreen.name)
      ? 'valid'
      : 'invalid'

  // Pass only template name, preset and content from template to screen, to avoid setting unintentional props, like thumbnail
  const onTemplateChange = ({ template, preset, content }) =>
    setLocalScreen((s) => ({ ...s, template, preset, content }))

  return (
    <Dialog
      title='New Screen'
      confirmLabel='Create'
      cancelLabel='Cancel'
      onConfirm={() => onSubmit(localScreen)}
      confirmDisabled={nameValidationState !== 'valid' || !localScreen.template}
      className='ScreenSettingsDialog'
      {...otherProps}
      role='dialog'
    >
      <Form>
        <FormItem label='Title'>
          <Textfield
            value={localScreen.title}
            onChange={(title) => setLocalScreen((s) => ({ ...s, title, name: kebabCase(title) }))}
          />
        </FormItem>
        <FormItem label='Name'>
          <Textfield
            value={localScreen.name}
            onChange={(name) => setLocalScreen((s) => ({ ...s, name }))}
            validationState={nameValidationState}
          />
        </FormItem>
      </Form>
      <ScreenTemplatePicker
        projectConfig={projectConfig}
        value={localScreen}
        allowFreeform={true}
        onChange={onTemplateChange}
      />
    </Dialog>
  )
}

const ScreenStructure = () => {
  const { screens, addScreen, moveScreen } = useScreens()
  const { config, canEdit } = useProject()
  const [selectedScreen, setSelectedScreen] = useState(null)

  const onSelect = (screen) => (evt) => {
    setSelectedScreen({ name: screen.meta.name, title: screen.meta.title })
    if (evt) {
      evt.stopPropagation()
    }
  }

  return (
    <div className={'ScreenStructure'}>
      <div className={'ScreenStructure__workspace'} onClick={() => setSelectedScreen(null)}>
        <ModalTrigger>
          <Button disabled={!canEdit}>+ Add Screen</Button>
          <NewScreenDialog projectScreens={screens} projectConfig={config} onSubmit={addScreen} />
        </ModalTrigger>
        {screens && (
          <DroppableArea accept={'screen'} onMove={moveScreen}>
            {screens.map((screen) => (
              <Draggable
                id={screen.meta.name}
                key={screen.meta.name}
                type={'screen'}
                position={screen.editor?.position}
                zIndex={screen.editor?.zIndex}
                canDrag={canEdit}
              >
                <Screen screen={screen} onSelect={onSelect} isSelected={screen.meta.name === selectedScreen?.name} />
              </Draggable>
            ))}
          </DroppableArea>
        )}
      </div>
      <ScreenSidebar
        key={selectedScreen?.name}
        screenName={selectedScreen?.name}
        screenTitle={selectedScreen?.title}
        setSelectedScreen={setSelectedScreen}
      />
    </div>
  )
}

export default ScreenStructure
